import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search, RefreshCw, X, UserCog, HeartPulse, Building2, Phone, Mail, MapPin,
  KeyRound, Copy, ChevronRight, ChevronDown, Users as UsersIcon
} from 'lucide-react';
import { apiClient, AdminPatientRow, AdminPatientSearchResponse, AdminPatientDetail } from '../lib/api';
import { US_STATE_OPTIONS } from '../lib/usStates';
import { formatCurrency } from '../lib/format';
import { toast } from '../lib/toast';

/**
 * Admin "All Patients": every clinic's patients in one place, for support.
 *
 * Read-only. To change anything, "Open as clinic" impersonates the patient's
 * clinic and lands on that patient's page, so edits go through the clinic's
 * own screens and audit trail. Opening a record here is itself logged
 * server-side (AdminAuditLog patient_viewed).
 *
 * Filters live in the URL so a support link ("look at ?q=ORD-…") opens the
 * same view for whoever it's sent to.
 */

const PAGE_SIZE = 25;

// Dates of birth are stored as UTC midnight; formatting them in local time
// shows the day before for anyone west of UTC.
const formatDob = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—';
const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('en-US') : '—';

const statusPill = (status: string) =>
  status === 'active'
    ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-gray-100 text-gray-600 border-gray-200';

export default function AdminPatients() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const clinicId = searchParams.get('clinic') || '';
  const state = searchParams.get('state') || '';
  const status = searchParams.get('status') || '';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const openId = searchParams.get('patient') || '';

  // The box is local so typing doesn't push a history entry per keystroke.
  const [searchInput, setSearchInput] = useState(q);
  const [data, setData] = useState<AdminPatientSearchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The clinic filter chip needs a name even when no row on screen has it.
  const [clinicLabel, setClinicLabel] = useState<string>('');
  // Filters live in the URL rather than useServerList, so the stale-response
  // guard is done here: only the newest request may write the results.
  const loadSeq = useRef(0);

  const updateParams = useCallback((patch: Record<string, string | null>, resetPage = true) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(patch)) {
        if (value) next.set(key, value); else next.delete(key);
      }
      if (resetPage && !('page' in patch)) next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => { setSearchInput(q); }, [q]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== q) updateParams({ q: searchInput.trim() || null });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, updateParams]);

  const load = useCallback(async () => {
    const mine = ++loadSeq.current;
    setLoading(true);
    setError(null);
    const response = await apiClient.getAdminPatients({
      q, physicianId: clinicId, state, status, page, limit: PAGE_SIZE
    });
    if (mine !== loadSeq.current) return;
    if (response.success && response.data) {
      setData(response.data);
    } else {
      setData(null);
      setError(response.error || 'Failed to search patients');
    }
    setLoading(false);
  }, [q, clinicId, state, status, page]);

  useEffect(() => { void load(); }, [load]);

  const filterByClinic = (row: AdminPatientRow) => {
    if (!row.clinic) return;
    setClinicLabel(row.clinic.clinicName);
    updateParams({ clinic: row.clinic._id });
  };

  const total = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.totalPages ?? 1;

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <HeartPulse className="w-6 h-6" /> All Patients
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Every clinic's patients. Search by name, email, phone, or paste an order or invoice number.
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Name, email, phone, ORD-… or INV-…"
              aria-label="Search patients"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-md text-sm"
              autoFocus
            />
          </div>
          <select
            value={status}
            onChange={e => updateParams({ status: e.target.value || null })}
            aria-label="Status"
            className="px-3 py-2 border border-gray-200 rounded-md text-sm"
          >
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={state}
            onChange={e => updateParams({ state: e.target.value || null })}
            aria-label="State"
            className="px-3 py-2 border border-gray-200 rounded-md text-sm"
          >
            <option value="">Any state</option>
            {US_STATE_OPTIONS.map(s => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
        </div>
        {clinicId && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-900 text-white border border-gray-900">
              <Building2 className="w-3.5 h-3.5" /> {clinicLabel || data?.rows[0]?.clinic?.clinicName || 'One clinic'}
              <button
                onClick={() => { setClinicLabel(''); updateParams({ clinic: null }); }}
                aria-label="Clear clinic filter"
                className="ml-0.5 opacity-70 hover:opacity-100"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 text-xs text-gray-500">
          <span>{loading ? 'Searching…' : `${total.toLocaleString()} patient record${total === 1 ? '' : 's'}`}</span>
          {data && q && (
            <span title={data.engine === 'atlas' ? 'Typo-tolerant search (Atlas Search)' : undefined}>
              {data.engine === 'reference' ? 'Matched by order / invoice / id'
                : data.engine === 'atlas' ? 'Fuzzy match'
                : 'Exact-prefix match'}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Patient</th>
                <th className="text-left px-4 py-2 font-medium">Contact</th>
                <th className="text-left px-4 py-2 font-medium">Clinic</th>
                <th className="text-left px-4 py-2 font-medium">Location</th>
                <th className="text-left px-4 py-2 font-medium">Orders</th>
                <th className="text-left px-4 py-2 font-medium">Added</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && !data && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Loading…</td></tr>
              )}
              {error && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-red-600">{error}</td></tr>
              )}
              {!loading && !error && data?.rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">No patients match.</td></tr>
              )}
              {data?.rows.map(row => (
                <tr
                  key={row._id}
                  onClick={() => updateParams({ patient: row._id }, false)}
                  className={`cursor-pointer hover:bg-gray-50 ${openId === row._id ? 'bg-indigo-50/60' : ''} ${loading ? 'opacity-60' : ''}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      {row.firstName} {row.lastName}
                      {row.status !== 'active' && (
                        <span className={`px-1.5 py-0.5 rounded border text-[11px] ${statusPill(row.status)}`}>{row.status}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">DOB {formatDob(row.dateOfBirth)}</div>
                    {row.otherClinicRecords > 0 && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        <UsersIcon className="w-3 h-3" /> Also at {row.otherClinicRecords} other clinic{row.otherClinicRecords === 1 ? '' : 's'}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <div>{row.phone}</div>
                    <div className="text-xs text-gray-500 truncate max-w-[220px]">{row.email || '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    {row.clinic ? (
                      <>
                        <button
                          onClick={e => { e.stopPropagation(); filterByClinic(row); }}
                          className="text-gray-900 hover:text-indigo-700 hover:underline text-left"
                          title="Show only this clinic's patients"
                        >
                          {row.clinic.clinicName}
                        </button>
                        <div className="text-xs text-gray-500">{row.clinic.accountManager ? `AM: ${row.clinic.accountManager.name}` : 'No AM'}</div>
                      </>
                    ) : <span className="text-gray-400">Unknown clinic</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{[row.city, row.state].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-4 py-3 text-gray-700">
                    <div>{row.orderCount}</div>
                    <div className="text-xs text-gray-500">{row.lastOrderAt ? `Last ${formatDate(row.lastOrderAt)}` : 'None yet'}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{formatDate(row.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
            <span className="text-gray-500">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => updateParams({ page: String(page - 1) }, false)}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >Previous</button>
              <button
                disabled={page >= totalPages}
                onClick={() => updateParams({ page: String(page + 1) }, false)}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {openId && (
        <PatientPanel
          patientId={openId}
          onClose={() => updateParams({ patient: null }, false)}
          onOpenRecord={id => updateParams({ patient: id }, false)}
        />
      )}
    </div>
  );
}

function Section({ title, count, children, defaultOpen = true }: { title: string; count?: number; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-t border-gray-200">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold text-gray-900">
        <span>{title}{count !== undefined && <span className="ml-1.5 text-gray-400 font-normal">{count}</span>}</span>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-4">{children}</div>}
    </section>
  );
}

function PatientPanel({ patientId, onClose, onOpenRecord }: { patientId: string; onClose: () => void; onOpenRecord: (id: string) => void }) {
  const [detail, setDetail] = useState<AdminPatientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    apiClient.getAdminPatient(patientId).then(response => {
      if (cancelled) return;
      if (response.success && response.data) setDetail(response.data);
      else setError(response.error || 'Failed to load patient');
    });
    return () => { cancelled = true; };
  }, [patientId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Same token swap as the Users page impersonation, but lands on this
  // patient's page inside the clinic's account.
  const openAsClinic = async () => {
    if (!detail?.clinic) return;
    setStarting(true);
    const response = await apiClient.startImpersonation(
      detail.clinic._id,
      reason.trim() || `Support: patient ${detail.patient.firstName} ${detail.patient.lastName}`
    );
    if (response.success && response.data) {
      const currentToken = localStorage.getItem('bloom_token');
      if (currentToken) localStorage.setItem('bloom_admin_token', currentToken);
      localStorage.setItem('bloom_token', response.data.token);
      localStorage.removeItem('bloom-auth');
      sessionStorage.clear();
      window.location.replace(`/patients/${detail.patient._id}`);
    } else {
      toast.error(response.error || 'Failed to open as clinic');
      setStarting(false);
    }
  };

  const copy = (text: string) => {
    void navigator.clipboard?.writeText(text).then(() => toast.success('Copied'));
  };

  const p = detail?.patient;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="relative w-full max-w-2xl h-full bg-white shadow-xl overflow-y-auto" role="dialog" aria-label="Patient details">
        <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
          <div className="px-5 py-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              {p ? (
                <>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    {p.firstName} {p.lastName}
                    <span className={`px-1.5 py-0.5 rounded border text-[11px] font-medium ${statusPill(p.status)}`}>{p.status}</span>
                  </h2>
                  <p className="text-sm text-gray-500">
                    DOB {formatDob(p.dateOfBirth)}{p.sex ? ` · ${p.sex}` : ''}{p.patientType === 'animal' && detail?.patient.animal ? ` · ${detail.patient.animal.species} "${detail.patient.animal.name}"` : ''}
                  </p>
                </>
              ) : <h2 className="text-lg font-semibold text-gray-400">{error ? 'Patient' : 'Loading…'}</h2>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {detail?.clinic && (
                <button
                  onClick={() => setConfirming(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-amber-500 text-white hover:bg-amber-600"
                >
                  <UserCog className="w-4 h-4" /> Open as clinic
                </button>
              )}
              <button onClick={onClose} aria-label="Close" className="p-2 rounded-md hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
          {/* Inside the pinned header so it's visible however far the panel is scrolled. */}
          {confirming && detail?.clinic && (
            <div className="mx-5 mb-4 p-4 rounded-lg border border-amber-200 bg-amber-50 space-y-3">
              <p className="text-sm text-amber-800">
                You'll be signed in as <strong>{detail.clinic.clinicName}</strong> and taken to this patient's page. Changes you make are recorded as the clinic's, with you as the actor.
              </p>
              <input
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Reason (optional), e.g. ticket number"
                className="w-full px-3 py-2 border border-amber-200 rounded-md text-sm bg-white"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirming(false)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-md bg-white">Cancel</button>
                <button
                  onClick={() => void openAsClinic()}
                  disabled={starting}
                  className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white disabled:opacity-60"
                >
                  {starting ? 'Opening…' : 'Continue as clinic'}
                </button>
              </div>
            </div>
          )}
        </div>

        {error && <div className="m-5 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">{error}</div>}

        {detail && p && (
          <div className="pb-8">
            <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-700">
                  <Phone className="w-4 h-4 text-gray-400" /> {p.phone}
                  <button onClick={() => copy(p.phone)} aria-label="Copy phone" className="text-gray-400 hover:text-gray-600"><Copy className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex items-center gap-2 text-gray-700 min-w-0">
                  <Mail className="w-4 h-4 text-gray-400 shrink-0" /> <span className="truncate">{p.email || '—'}</span>
                  {p.email && <button onClick={() => copy(p.email!)} aria-label="Copy email" className="text-gray-400 hover:text-gray-600"><Copy className="w-3.5 h-3.5" /></button>}
                </div>
                <div className="flex items-start gap-2 text-gray-700">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <span>
                    {p.address?.street}{p.address?.street2 ? `, ${p.address.street2}` : ''}<br />
                    {p.address?.city}, {p.address?.state} {p.address?.zipCode}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-start gap-2 text-gray-700">
                  <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
                  <span>
                    {detail.clinic?.clinicName || 'Unknown clinic'}
                    {detail.clinic && <span className="block text-xs text-gray-500">{detail.clinic.physicianName} · {detail.clinic.email}</span>}
                    {detail.clinic?.accountManager && <span className="block text-xs text-gray-500">AM: {detail.clinic.accountManager.name}</span>}
                  </span>
                </div>
                <div className="flex items-start gap-2 text-gray-700">
                  <KeyRound className="w-4 h-4 text-gray-400 mt-0.5" />
                  <span>
                    {detail.portalAccount.exists
                      ? <>Portal account{detail.portalAccount.passwordSet ? ' · password set' : ' · no password'}{detail.portalAccount.passkeyCount ? ` · ${detail.portalAccount.passkeyCount} passkey${detail.portalAccount.passkeyCount === 1 ? '' : 's'}` : ''}</>
                      : 'No portal sign-in yet'}
                    {detail.portalAccount.locked && <span className="block text-xs text-red-600 font-medium">Locked after failed sign-ins</span>}
                  </span>
                </div>
                <div className="text-xs text-gray-500 pl-6">
                  Added {formatDate(p.createdAt)}{p.signupSource ? ` via ${p.signupSource.replace(/_/g, ' ')}` : ''}
                </div>
              </div>
            </div>

            {detail.otherClinicRecords.length > 0 && (
              <Section title="Same phone at other clinics" count={detail.otherClinicRecords.length}>
                <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                  {detail.otherClinicRecords.map(r => (
                    <li key={r._id}>
                      <button onClick={() => onOpenRecord(r._id)} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 text-left">
                        <span>
                          <span className="font-medium text-gray-900">{r.firstName} {r.lastName}</span>
                          <span className="text-gray-500"> · {r.clinic?.clinicName || 'Unknown clinic'}</span>
                        </span>
                        <span className="flex items-center gap-2 text-xs text-gray-500">
                          {r.status !== 'active' && r.status} Added {formatDate(r.createdAt)} <ChevronRight className="w-4 h-4" />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            <Section title="Orders" count={detail.orders.length}>
              {detail.orders.length === 0 ? <p className="text-sm text-gray-500">No orders.</p> : (
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500">
                    <tr>
                      <th className="text-left py-1 font-medium">Order</th>
                      <th className="text-left py-1 font-medium">Items</th>
                      <th className="text-left py-1 font-medium">Status</th>
                      <th className="text-right py-1 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {detail.orders.map(o => (
                      <tr key={o._id}>
                        <td className="py-2 pr-2 align-top">
                          <button onClick={() => copy(o.orderNumber)} className="font-mono text-xs text-gray-900 hover:text-indigo-700 text-left whitespace-nowrap" title="Copy order number">{o.orderNumber}</button>
                          <div className="text-xs text-gray-500">{formatDate(o.createdAt)}</div>
                        </td>
                        <td className="py-2 pr-2 align-top text-gray-700">
                          {o.items?.map(i => `${i.productName}${i.quantity > 1 ? ` ×${i.quantity}` : ''}`).join(', ') || '—'}
                        </td>
                        <td className="py-2 pr-2 align-top">
                          <div className="text-gray-700">{o.status.replace(/_/g, ' ')}</div>
                          {o.paymentDetails?.status && <div className="text-xs text-gray-500">payment {o.paymentDetails.status}</div>}
                          {o.trackingNumber && <div className="text-xs text-gray-500 font-mono">{o.trackingNumber}</div>}
                        </td>
                        <td className="py-2 align-top text-right text-gray-900">{o.totalAmount !== undefined ? formatCurrency(o.totalAmount) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            <Section title="Invoices" count={detail.invoices.length} defaultOpen={detail.invoices.length > 0}>
              {detail.invoices.length === 0 ? <p className="text-sm text-gray-500">No invoices.</p> : (
                <ul className="divide-y divide-gray-200 text-sm">
                  {detail.invoices.map(inv => (
                    <li key={inv._id} className="py-2 flex items-center justify-between">
                      <span>
                        <button onClick={() => copy(inv.invoiceNumber)} className="font-mono text-xs text-gray-900 hover:text-indigo-700 text-left whitespace-nowrap" title="Copy invoice number">{inv.invoiceNumber}</button>
                        <span className="text-xs text-gray-500"> · {formatDate(inv.createdAt)} · {inv.status}</span>
                      </span>
                      <span className="text-right">
                        <span className="text-gray-900">{formatCurrency(inv.totalAmount)}</span>
                        {inv.amountDue > 0 && <span className="block text-xs text-red-600">{formatCurrency(inv.amountDue)} due</span>}
                        {!!inv.telemedNetworkFee && <span className="block text-xs text-gray-500">incl. {formatCurrency(inv.telemedNetworkFee)} telemed fee</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {detail.labOrders.length > 0 && (
              <Section title="Lab orders" count={detail.labOrders.length}>
                <ul className="divide-y divide-gray-200 text-sm">
                  {detail.labOrders.map(l => (
                    <li key={l._id} className="py-2 flex items-center justify-between">
                      <span>
                        <span className="font-mono text-xs text-gray-900">{l.panelCode}</span>
                        <span className="text-xs text-gray-500"> · {l.requisitionType.replace(/_/g, ' ')} · {formatDate(l.createdAt)}</span>
                      </span>
                      <span className="text-gray-700">{l.status.replace(/_/g, ' ')}{l.resultedAt ? ` ${formatDate(l.resultedAt)}` : ''}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {detail.memberships.length > 0 && (
              <Section title="Memberships" count={detail.memberships.length}>
                <ul className="divide-y divide-gray-200 text-sm">
                  {detail.memberships.map(m => (
                    <li key={m._id} className="py-2 flex items-center justify-between">
                      <span className="text-gray-900">{m.tier} · {m.productCount} product{m.productCount === 1 ? '' : 's'} · {formatCurrency(m.pricePerCycleCents / 100)}/cycle</span>
                      <span className="text-gray-700">{m.status}{m.nextBillingDate ? ` · next ${formatDate(m.nextBillingDate)}` : ''}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {p.screeningAnswers && Object.keys(p.screeningAnswers).length > 0 && (
              <Section title="Screening answers" defaultOpen={false}>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {Object.entries(p.screeningAnswers).map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-2 border-b border-gray-200 py-1">
                      <dt className="text-gray-500">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}</dt>
                      <dd className="text-gray-900 text-right">{typeof value === 'boolean' ? (value ? 'yes' : 'no') : String(value)}</dd>
                    </div>
                  ))}
                </dl>
                {p.screeningAnswersUpdatedAt && <p className="mt-2 text-xs text-gray-500">Updated {formatDate(p.screeningAnswersUpdatedAt)}</p>}
              </Section>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
